import { useMemo, useState } from "react";

import { ChevronDown, ChevronRight, Search } from "lucide-react";

import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { getWorkingDaysInfo } from "~/utils/workingDays";

import {
  type AnalysisViewMode,
  ClientGoalRow,
  MONTH_LABELS,
  SellerMonthlyGoals,
  computeYearlyAggregates,
} from "../types/salesGoals";
import ClientAnalysisTable from "./ClientAnalysisTable";
import ScrollableTableWrapper from "./ScrollableTableWrapper";

interface SellerClientTabProps {
  clients: ClientGoalRow[];
  sellers: SellerMonthlyGoals[];
  currentMonth: number;
  isLoading: boolean;
  viewMode: AnalysisViewMode;
  selectedMonth: number;
  year: number;
  hideSelllerFilter?: boolean;
}

function formatCLP(amount: number): string {
  return "$" + Math.round(amount).toLocaleString("es-CL");
}

function formatPct(pct: number): string {
  return `${Math.round(pct)}%`;
}

function pctColor(pct: number): string {
  if (pct >= 80) return "text-emerald-600";
  if (pct >= 50) return "text-orange-500";
  return "text-red-500";
}

export default function SellerClientTab({
  clients,
  sellers,
  currentMonth,
  isLoading,
  viewMode,
  selectedMonth,
  year,
  hideSelllerFilter,
}: SellerClientTabProps) {
  if (viewMode === "monthly") {
    return (
      <ClientAnalysisTable
        clients={clients}
        sellers={sellers}
        selectedMonth={selectedMonth}
        year={year}
        groupBy="seller"
        isLoading={isLoading}
      />
    );
  }
  const [selectedSeller, setSelectedSeller] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSellers, setExpandedSellers] = useState<Set<string>>(
    new Set()
  );

  const toggleSeller = (name: string) => {
    setExpandedSellers((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const grouped = useMemo(() => {
    let filtered =
      selectedSeller === "all"
        ? clients
        : clients.filter((c) => c.seller_name === selectedSeller);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((c) =>
        c.client_name.toLowerCase().includes(q)
      );
    }

    const groups: Record<string, ClientGoalRow[]> = {};
    for (const c of filtered) {
      const key = c.seller_name || "Sin Vendedor";
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    }
    return groups;
  }, [clients, selectedSeller, searchQuery]);

  if (isLoading) {
    return <TableSkeleton rows={8} columns={8} />;
  }

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentMonthWd =
    currentMonth > 0 ? getWorkingDaysInfo(year, currentMonth) : undefined;
  const sellerNames = [...new Set(sellers.map((s) => s.seller_name))].sort();
  const cellBorder = "border-r border-border";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        {!hideSelllerFilter && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-muted-foreground">
              Vendedor:
            </span>
            <Select value={selectedSeller} onValueChange={setSelectedSeller}>
              <SelectTrigger className="h-9 w-full sm:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {sellerNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar cliente..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-9 w-full pl-9"
          />
        </div>
      </div>

      <ScrollableTableWrapper>
        <TableHeader>
          <TableRow>
            <TableHead
              className={`sticky left-0 z-10 min-w-[200px] whitespace-nowrap bg-card ${cellBorder}`}
            >
              Cliente
            </TableHead>
            {months.map((m) => (
              <TableHead
                key={m}
                className={`min-w-[100px] whitespace-nowrap text-right ${cellBorder}`}
              >
                {MONTH_LABELS[m]}
              </TableHead>
            ))}
            <TableHead
              className={`min-w-[120px] whitespace-nowrap text-right font-bold ${cellBorder}`}
            >
              Total
            </TableHead>
            <TableHead
              className={`min-w-[140px] whitespace-nowrap text-right font-bold ${cellBorder}`}
            >
              Deb. Llevar
            </TableHead>
            <TableHead
              className={`min-w-[70px] whitespace-nowrap text-right font-bold ${cellBorder}`}
            >
              %
            </TableHead>
            <TableHead className="min-w-[140px] whitespace-nowrap text-right font-bold">
              Cierre Est.
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(grouped).map(
            ([sellerName, sellerClients], groupIndex) => {
              const isExpanded = expandedSellers.has(sellerName);
              const subtotals: Record<number, number> = {};
              const subtotalActuals: Record<number, number> = {};
              let subtotalTotal = 0;
              let subtotalActualTotal = 0;
              for (const c of sellerClients) {
                for (const m of months) {
                  subtotals[m] = (subtotals[m] || 0) + (c.months[m] || 0);
                  subtotalActuals[m] =
                    (subtotalActuals[m] || 0) + (c.actuals[m] || 0);
                }
                subtotalTotal += c.total;
                subtotalActualTotal += c.totalActual;
              }

              return [
                <TableRow
                  key={`header-${sellerName}`}
                  style={{
                    animationDelay: `${Math.min(groupIndex * 40, 400)}ms`,
                  }}
                  className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/70"
                  onClick={() => toggleSeller(sellerName)}
                >
                  <TableCell
                    colSpan={months.length + 5}
                    className="sticky left-0 z-10 bg-muted/50 text-sm font-bold"
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      {sellerName} ({sellerClients.length} clientes)
                    </div>
                  </TableCell>
                </TableRow>,
                ...(isExpanded
                  ? [
                      ...sellerClients.map((client) => {
                        const agg = computeYearlyAggregates(
                          client.months,
                          client.actuals,
                          year,
                          currentMonth,
                          currentMonthWd
                        );
                        return (
                          <TableRow key={client.client_id}>
                            <TableCell
                              className={`sticky left-0 z-10 bg-card pl-6 font-medium ${cellBorder}`}
                            >
                              {client.client_name}
                            </TableCell>
                            {months.map((m) => {
                              const goal = client.months[m] || 0;
                              const actual = client.actuals[m] || 0;
                              const showActual =
                                m <= currentMonth && (goal > 0 || actual > 0);
                              return (
                                <TableCell
                                  key={m}
                                  className={`text-right tabular-nums ${cellBorder}`}
                                >
                                  <div className="text-blue-600">
                                    <span className="mr-1 text-xs text-muted-foreground">
                                      Meta:
                                    </span>
                                    {goal ? formatCLP(goal) : "-"}
                                  </div>
                                  {showActual && (
                                    <div className="text-xs text-red-500">
                                      <span className="mr-1 text-muted-foreground">
                                        Real:
                                      </span>
                                      {formatCLP(actual)}
                                    </div>
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell
                              className={`text-right font-semibold tabular-nums ${cellBorder}`}
                            >
                              <div className="text-blue-600">
                                <span className="mr-1 text-xs font-normal text-muted-foreground">
                                  Meta:
                                </span>
                                {formatCLP(client.total)}
                              </div>
                              {currentMonth > 0 && (
                                <div className="text-xs text-red-500">
                                  <span className="mr-1 font-normal text-muted-foreground">
                                    Real:
                                  </span>
                                  {formatCLP(client.totalActual)}
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              className={`text-right tabular-nums ${cellBorder}`}
                            >
                              {currentMonth > 0
                                ? formatCLP(agg.deberiaLlevar)
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={`text-right font-semibold tabular-nums ${cellBorder} ${currentMonth > 0 ? pctColor(agg.pctAchievement) : ""}`}
                            >
                              {currentMonth > 0
                                ? formatPct(agg.pctAchievement)
                                : "-"}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatCLP(agg.cierreEstimado)}
                            </TableCell>
                          </TableRow>
                        );
                      }),
                      (() => {
                        const subAgg = computeYearlyAggregates(
                          subtotals,
                          subtotalActuals,
                          year,
                          currentMonth,
                          currentMonthWd
                        );
                        return (
                          <TableRow
                            key={`subtotal-${sellerName}`}
                            className="border-b-2"
                          >
                            <TableCell
                              className={`sticky left-0 z-10 bg-muted/30 pl-6 text-xs font-semibold uppercase text-muted-foreground ${cellBorder}`}
                            >
                              Subtotal {sellerName}
                            </TableCell>
                            {months.map((m) => {
                              const goal = subtotals[m] || 0;
                              const actual = subtotalActuals[m] || 0;
                              const showActual =
                                m <= currentMonth && (goal > 0 || actual > 0);
                              return (
                                <TableCell
                                  key={m}
                                  className={`bg-muted/30 text-right font-semibold tabular-nums ${cellBorder}`}
                                >
                                  <div className="text-blue-600">
                                    <span className="mr-1 text-xs font-normal text-muted-foreground">
                                      Meta:
                                    </span>
                                    {goal ? formatCLP(goal) : "-"}
                                  </div>
                                  {showActual && (
                                    <div className="text-xs font-medium text-red-500">
                                      <span className="mr-1 font-normal text-muted-foreground">
                                        Real:
                                      </span>
                                      {formatCLP(actual)}
                                    </div>
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell
                              className={`bg-muted/30 text-right font-bold tabular-nums ${cellBorder}`}
                            >
                              <div className="text-blue-600">
                                <span className="mr-1 text-xs font-normal text-muted-foreground">
                                  Meta:
                                </span>
                                {formatCLP(subtotalTotal)}
                              </div>
                              {currentMonth > 0 && (
                                <div className="text-xs font-medium text-red-500">
                                  <span className="mr-1 font-normal text-muted-foreground">
                                    Real:
                                  </span>
                                  {formatCLP(subtotalActualTotal)}
                                </div>
                              )}
                            </TableCell>
                            <TableCell
                              className={`bg-muted/30 text-right font-bold tabular-nums ${cellBorder}`}
                            >
                              {currentMonth > 0
                                ? formatCLP(subAgg.deberiaLlevar)
                                : "-"}
                            </TableCell>
                            <TableCell
                              className={`bg-muted/30 text-right font-bold tabular-nums ${cellBorder} ${currentMonth > 0 ? pctColor(subAgg.pctAchievement) : ""}`}
                            >
                              {currentMonth > 0
                                ? formatPct(subAgg.pctAchievement)
                                : "-"}
                            </TableCell>
                            <TableCell className="bg-muted/30 text-right font-bold tabular-nums">
                              {formatCLP(subAgg.cierreEstimado)}
                            </TableCell>
                          </TableRow>
                        );
                      })(),
                    ]
                  : []),
              ];
            }
          )}
        </TableBody>
      </ScrollableTableWrapper>
    </div>
  );
}
